/** RFC 0279 S1: a package copied out of its World, with no live document ids. */
import {
  type AnyDocumentId,
  isValidDocumentId,
  type Repo,
} from '@automerge/automerge-repo/slim'
import {
  createDocument,
  publishArtifact,
  writeDocumentContent,
} from '../documents/mutations'
import { projectDocuments, readDocumentContent } from '../documents/reads'
import {
  changeProject,
  plainProjectEntry,
  projectString,
} from '../project/scalars'
import type { ProjectDoc } from '../project/types'
import type { WorldDoc } from '../world/types'
import {
  cleanIconSpec,
  generateIcon,
  type IconSpec,
  iconSpecError,
} from './app-icon'
import {
  installChrome,
  installLayout,
  installRenderer,
  registerApp,
  setLayoutGeometry,
  setLayoutSlots,
  updateApp,
} from './mutations'
import type {
  AppCapabilities,
  AppDoc,
  AppPresentation,
  AppSchemaSpec,
  Layout,
  SettingSpec,
  Slot,
  SlotFill,
} from './types'

export interface PackageRelease {
  lineageId: string
  version: string
  kind: 'app' | 'thread' | 'layout' | Slot
  name: string
  description: string
  page: { category: string; about: string; screenshots: string[] }
  publisher: { id: string; name: string }
  publishedAt: number
  dataType?: string
  schema?: AppSchemaSpec
  capabilities: AppCapabilities
  handles?: string[]
  presentation?: AppPresentation
  instancesOpenable?: boolean
  realm?: 'host'
  sync?: 'documents'
  panels?: SlotFill[]
  settings?: SettingSpec[]
  icon?: IconSpec
  workerName?: string
  runtime: string
  view?: { name: string; mime: string; content: string }
  layout?: Layout
  skill?: { content: string }
  tools?: { name: string; description: string }[]
  sourceReleaseId?: string
}

export interface ReleaseDetails {
  lineageId: string
  version: string
  page: PackageRelease['page']
  publisher: PackageRelease['publisher']
  publishedAt?: number
}

function plain<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T
}

/**
 * The document a release keeps at `path`, reused when one is already there.
 * Installing or updating to a version twice used to add a second `view` and
 * `skill.md` at the same path; a path has one document (RFC 0357 3i).
 */
async function releaseDocument(
  repo: Repo,
  projectId: string,
  init: { path: string; mime: string; title: string; content?: string },
): Promise<string> {
  const project = await loaded<ProjectDoc>(repo, projectId)
  const existing = projectDocuments(project).find(
    (doc) => doc.path === init.path,
  )
  if (!existing) {
    const created = await createDocument(repo, projectId, init)
    return created.documentId as string
  }
  if (init.content !== undefined)
    await writeDocumentContent(repo, projectId, existing.id, init.content)
  return existing.id
}

async function loaded<T>(repo: Repo, id: string): Promise<T> {
  const handle = await repo.find<T>(id as AnyDocumentId)
  await (handle as { whenReady?: () => Promise<void> }).whenReady?.()
  const doc = handle.doc()
  if (!doc) throw new Error(`Package document ${id} is missing`)
  return doc
}

/**
 * The tile the window draws for this app, as a spec another World can draw.
 *
 * The window never draws `ref.icon` alone: it fills what the entry leaves out
 * from the house icons (by data type or name) and from the app's id and words
 * (`generateIcon`). The first-party apps were registered before icons were
 * stored, so for them the entry holds nothing and the whole tile is filled in.
 * The release carries the filled spec, so its colour no longer depends on a
 * local app id that means nothing on the registry. The code stays out unless
 * the app set one: codes are made distinct per catalog, at draw time.
 */
function releaseIcon(
  appId: string,
  ref: {
    name: string
    description?: string
    dataType?: string
    icon?: unknown
  },
): IconSpec {
  const own = ref.icon === undefined ? undefined : plain(ref.icon)
  return cleanIconSpec(
    generateIcon(
      {
        id: appId,
        name: ref.name,
        ...(ref.dataType ? { dataType: ref.dataType } : {}),
        ...(ref.description ? { description: ref.description } : {}),
      },
      own && !iconSpecError(own) ? (own as IconSpec) : undefined,
    ),
  )
}

/** Copy the installed package and its view/skill bytes into a portable value. */
export async function releaseFromApp(
  repo: Repo,
  catalogId: string,
  appId: string,
  details: ReleaseDetails,
): Promise<PackageRelease> {
  const catalog = await loaded<ProjectDoc>(repo, catalogId)
  // Plain strings out of a scalar entry (RFC 0357 S11): every field below is
  // compared or handed to repo.find.
  const ref = catalog.apps?.[appId] && plainProjectEntry(catalog.apps[appId])
  if (!ref) throw new Error(`Package ${appId} is not installed in ${catalogId}`)
  const app = await loaded<
    Partial<AppDoc> & { settings?: SettingSpec[]; layout?: Layout }
  >(repo, appId)
  const handlerOnlyApp =
    !ref.dataType && ref.handles?.some((handle) => handle !== 'agent')
  const fill =
    !ref.dataType && !handlerOnlyApp
      ? ref.panels?.find((p) => p.kind === 'view')
      : undefined
  const kind: PackageRelease['kind'] =
    ref.dataType || handlerOnlyApp
      ? 'app'
      : ref.presentation === 'layout'
        ? 'layout'
        : ref.handles?.includes('agent')
          ? 'thread'
          : (fill?.slot ?? 'panel')
  if (
    !ref.dataType &&
    !handlerOnlyApp &&
    kind !== 'layout' &&
    kind !== 'thread' &&
    !fill
  ) {
    throw new Error(
      `Cannot release ${ref.name}: it has no data type or chrome slot`,
    )
  }
  const view = ref.viewDocId
    ? await loaded<{ mime?: string; path?: string; content?: unknown }>(
        repo,
        ref.viewDocId,
      )
    : undefined
  if (kind !== 'layout' && !view) {
    throw new Error(`Cannot release ${ref.name}: it has no view document`)
  }
  const skill = ref.skillDocId
    ? await loaded<{ content?: unknown }>(repo, ref.skillDocId)
    : undefined
  // installRelease refuses an app with no skill, so refuse it here instead of
  // posting a release that no World can install.
  if (kind === 'app' && !skill) {
    throw new Error(`Cannot release ${ref.name}: it has no skill document`)
  }
  const release: PackageRelease = {
    lineageId: details.lineageId,
    version: details.version,
    kind,
    name: ref.name,
    description: ref.description,
    page: plain(details.page),
    publisher: plain(details.publisher),
    publishedAt: details.publishedAt ?? Date.now(),
    capabilities: plain(app.capabilities ?? { writes: [], reads: [] }),
    runtime: app.runtime ?? (kind === 'app' ? 'html' : 'host'),
  }
  if (view) {
    release.view = {
      name: view.path ?? `${ref.name}.${kind === 'app' ? 'html' : 'js'}`,
      mime: view.mime ?? (kind === 'app' ? 'text/html' : 'text/javascript'),
      content: readDocumentContent(view),
    }
  }
  if (kind === 'layout') {
    const layout = ref.layout ?? app.layout
    if (!layout) throw new Error(`Cannot release ${ref.name}: it has no layout`)
    // A local layout's tree is a snapshot of its author's open documents and
    // sessions. Package layouts carry only reusable chrome choices and shape.
    const portable: Layout = {
      slots: plain(layout.slots),
      ...(layout.geometry ? { geometry: plain(layout.geometry) } : {}),
    }
    for (const [slot, ids] of Object.entries(portable.slots)) {
      if (!ids) continue
      portable.slots[slot as Slot] = await Promise.all(
        ids.map(async (id) => {
          if (!isValidDocumentId(id)) return id
          const dependency = catalog.apps?.[id]
          if (!dependency) {
            throw new Error(
              `Cannot release ${ref.name}: layout ${slot} names an unknown local fill`,
            )
          }
          const fill = await loaded<Partial<AppDoc>>(repo, id)
          const lineage =
            fill.publishedAs?.lineageId ?? fill.installedFrom?.lineageId
          if (!lineage) {
            throw new Error(
              `Cannot release ${ref.name}: publish ${dependency.name} before this layout`,
            )
          }
          return lineage
        }),
      )
    }
    release.layout = portable
  }
  if (kind === 'app') {
    release.schema = plain(app.schema ?? { fields: {} })
    if (ref.dataType) release.dataType = ref.dataType
  }
  if (skill) release.skill = { content: readDocumentContent(skill) }
  if (ref.handles?.length) release.handles = plain(ref.handles)
  if (ref.presentation) release.presentation = ref.presentation
  if (ref.instancesOpenable) release.instancesOpenable = true
  if (ref.realm) release.realm = ref.realm
  if (ref.sync) release.sync = ref.sync
  if (app.workerName) release.workerName = app.workerName
  release.icon = releaseIcon(appId, ref)
  if (ref.settings?.length) release.settings = plain(ref.settings)
  if (ref.panels?.length) {
    release.panels = plain(ref.panels).map((p) => ({
      ...p,
      id: p.id === appId ? details.lineageId : p.id,
    }))
    if (release.panels.some((p) => isValidDocumentId(p.id))) {
      throw new Error(
        `Cannot release ${ref.name}: a panel still names a local document`,
      )
    }
  }
  // The result is deliberately JSON-safe: no Automerge values or source ids.
  return plain(release)
}

export interface InstallReleaseTarget {
  /** Project that holds the new view and skill documents. */
  projectId: string
  /** The World the project is in. Its catalog is always checked for a type
   *  clash, so a project install cannot claim a type a World app owns. */
  worldId?: string
  /** Where the catalog entry goes. Defaults to the World when one is given,
   *  which installs the package for every project in it. */
  scope?: 'world' | 'project'
}

export class WorldInstallProjectUnavailableError extends Error {
  constructor(readonly projectId: string) {
    super(
      `Could not check project ${projectId} for a record type clash. World install stopped. Choose This project to install here instead.`,
    )
    this.name = 'WorldInstallProjectUnavailableError'
  }
}

async function siblingCatalog(
  repo: Repo,
  projectId: string,
): Promise<ProjectDoc> {
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    return await Promise.race([
      loaded<ProjectDoc>(repo, projectId),
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new Error('Timed out')), 15_000)
      }),
    ])
  } catch {
    throw new WorldInstallProjectUnavailableError(projectId)
  } finally {
    if (timer) clearTimeout(timer)
  }
}

/** Install the same bytes a local push would use, without source or a build. */
export async function installRelease(
  repo: Repo,
  target: InstallReleaseTarget,
  release: PackageRelease,
  /** The registry id of these bytes, recorded as `installedFrom` (RFC 0279 3e). */
  releaseId?: string,
): Promise<string> {
  const appId = await installReleaseBytes(repo, target, release)
  if (releaseId) {
    const handle = await repo.find<AppDoc>(appId as AnyDocumentId)
    handle.change((d) => {
      d.installedFrom = {
        lineageId: release.lineageId,
        version: release.version,
        releaseId,
      }
    })
  }
  return appId
}

async function installReleaseBytes(
  repo: Repo,
  target: InstallReleaseTarget,
  release: PackageRelease,
): Promise<string> {
  const catalogId =
    target.worldId && target.scope !== 'project'
      ? target.worldId
      : target.projectId
  if (!release.lineageId || !release.name) {
    throw new Error('Release is missing its lineage or name')
  }
  // A release is untrusted JSON. Refuse a bad icon before any document is
  // written, rather than in registerApp after the view and skill exist.
  const iconError = iconSpecError(release.icon)
  if (iconError) throw new Error(iconError)
  if (release.kind === 'app') {
    if (
      !release.skill ||
      !release.view ||
      (release.dataType && !release.schema) ||
      (!release.dataType &&
        !release.handles?.some((handle) => handle !== 'agent'))
    ) {
      throw new Error(
        `App release ${release.name} needs a view and skill, and either a data type or a content handler`,
      )
    }
    // Check every catalog that shares a project with this one before creating
    // documents. A World app and a project app with one type would otherwise
    // compete even though each catalog alone passes registerApp's check. A
    // project install shares its project with the World; a World install
    // shares one with every project in it.
    if (release.dataType) {
      const catalogIds = new Set([catalogId, target.projectId])
      if (target.worldId) {
        catalogIds.add(target.worldId)
        if (catalogId === target.worldId) {
          const world = await loaded<WorldDoc>(repo, target.worldId)
          for (const id of Object.keys(
            world.projects ?? world.workspaces ?? {},
          ))
            catalogIds.add(id)
        }
      }
      for (const id of catalogIds) {
        const catalog =
          id !== catalogId && id !== target.projectId && id !== target.worldId
            ? await siblingCatalog(repo, id)
            : await loaded<ProjectDoc>(repo, id)
        for (const [otherId, other] of Object.entries(catalog.apps ?? {})) {
          if (projectString(other.dataType) === release.dataType) {
            throw new Error(
              `Cannot install "${release.name}": "${other.name}" (${otherId}) already owns dataType "${release.dataType}"`,
            )
          }
        }
      }
    }
    const dir = `packages/${encodeURIComponent(release.lineageId)}/${encodeURIComponent(release.version)}`
    const viewId = await releaseDocument(repo, target.projectId, {
      path: `${dir}/view`,
      mime: release.view.mime,
      title: `${release.name} view`,
    })
    await publishArtifact(repo, target.projectId, viewId, release.view.content)
    const skillId = await releaseDocument(repo, target.projectId, {
      path: `${dir}/skill.md`,
      mime: 'text/markdown',
      title: `${release.name} skill`,
      content: release.skill.content,
    })
    return registerApp(repo, target.projectId, {
      name: release.name,
      description: release.description,
      dataType: release.dataType ?? '',
      schema: plain(release.schema ?? { fields: {} }),
      viewDocId: viewId,
      skillDocId: skillId,
      runtime: release.runtime,
      capabilities: plain(release.capabilities),
      ...(release.handles ? { handles: plain(release.handles) } : {}),
      ...(release.panels ? { panels: plain(release.panels) } : {}),
      ...(release.presentation ? { presentation: release.presentation } : {}),
      ...(release.instancesOpenable ? { instancesOpenable: true } : {}),
      ...(release.realm ? { realm: release.realm } : {}),
      ...(release.sync ? { sync: release.sync } : {}),
      ...(release.icon ? { icon: plain(release.icon) } : {}),
      ...(release.workerName ? { workerName: release.workerName } : {}),
      catalogId,
    })
  }
  if (release.kind === 'layout') {
    if (!release.layout)
      throw new Error(`Layout release ${release.name} needs slots`)
    if (release.layout.tree !== undefined)
      throw new Error(
        `Layout release ${release.name} cannot contain local panes`,
      )
    return installLayout(repo, catalogId, {
      name: release.name,
      description: release.description,
      ...plain(release.layout),
    })
  }
  if (!release.view)
    throw new Error(`${release.kind} release ${release.name} needs a view`)
  if (release.kind === 'thread') {
    return (
      await installRenderer(repo, catalogId, {
        name: release.name,
        description: release.description,
        handles: release.handles?.[0] ?? 'agent',
        code: release.view.content,
      })
    ).appId
  }
  const slot: Slot = release.kind
  const fill = release.panels?.find((p) => p.slot === slot)
  const installed = await installChrome(repo, catalogId, {
    name: release.name,
    description: release.description,
    slot,
    fillId: fill?.id ?? release.lineageId,
    code: release.view.content,
    ...(release.settings ? { settings: plain(release.settings) } : {}),
  })
  return installed.appId
}

const SEMVER = /^(\d+)\.(\d+)\.(\d+)$/

/** Whether `next` is a later semver than `current`. Malformed is never newer. */
export function isNewerVersion(next: string, current: string): boolean {
  const a = SEMVER.exec(next)
  const b = SEMVER.exec(current)
  if (!a || !b) return false
  for (let i = 1; i <= 3; i++) {
    if (Number(a[i]) !== Number(b[i])) return Number(a[i]) > Number(b[i])
  }
  return false
}

/** What a release newly reaches. Only what the installed package lacked. */
export interface ReleaseReach {
  reads: string[]
  writes: string[]
  /** Newly runs in the window's own realm instead of a sandbox. */
  realm?: 'host'
  /** Newly syncs the project's documents into its frame. */
  sync?: 'documents'
}

/**
 * What a release can reach that the installed package cannot (RFC 0279 3f).
 *
 * Only additions: a version that reaches LESS needs no warning, and one that
 * reaches more must say what before the click. Moving into the window's own
 * realm, or starting to sync the project's documents, reaches more too.
 */
export function releaseReachesMore(
  current:
    | (AppCapabilities & { realm?: 'host'; sync?: 'documents' })
    | undefined,
  release: PackageRelease,
): ReleaseReach {
  const had = (list: string[] | undefined) => new Set(list ?? [])
  const reads = had(current?.reads)
  const writes = had(current?.writes)
  return {
    reads: (release.capabilities?.reads ?? []).filter((r) => !reads.has(r)),
    writes: (release.capabilities?.writes ?? []).filter((w) => !writes.has(w)),
    ...(release.realm === 'host' && current?.realm !== 'host'
      ? { realm: 'host' as const }
      : {}),
    ...(release.sync === 'documents' && current?.sync !== 'documents'
      ? { sync: 'documents' as const }
      : {}),
  }
}

/**
 * Take a newer release of an installed package (RFC 0279 S7).
 *
 * THE APP ID STAYS. Every record the package owns is found through its type,
 * and the type is refused if it changes, so the records keep rendering. New
 * view and skill documents are written beside the old ones rather than over
 * them: an app's view document is a published artifact, and replacing it in
 * place would change a version someone else may still be reading.
 */
export async function updateFromRelease(
  repo: Repo,
  target: { projectId: string; worldId?: string },
  appId: string,
  release: PackageRelease,
  releaseId: string,
): Promise<void> {
  const app = await loaded<AppDoc>(repo, appId)
  const from = app.installedFrom
  if (!from || from.lineageId !== release.lineageId) {
    throw new Error(
      `Cannot update ${app.name ?? appId}: it was not installed from ${release.lineageId}`,
    )
  }
  if (!isNewerVersion(release.version, from.version)) {
    throw new Error(
      `Cannot update ${app.name ?? appId}: ${release.version} is not newer than ${from.version}`,
    )
  }
  // Which catalog holds the entry: the World's for a World install, else the
  // project's. The update writes there, or the window keeps the old row.
  let catalogId = target.projectId
  if (target.worldId) {
    const world = await loaded<ProjectDoc>(repo, target.worldId)
    if (world.apps?.[appId]) catalogId = target.worldId
  }
  const catalog = await loaded<ProjectDoc>(repo, catalogId)
  const entry = catalog.apps?.[appId] && plainProjectEntry(catalog.apps[appId])
  if (!entry) throw new Error(`App ${appId} is not in its catalog`)
  if ((entry.dataType ?? '') !== (release.dataType ?? '')) {
    throw new Error(
      `Cannot update ${app.name ?? appId}: the new version changes its record type from "${entry.dataType ?? ''}" to "${release.dataType ?? ''}"`,
    )
  }
  const iconError = iconSpecError(release.icon)
  if (iconError) throw new Error(iconError)

  if (release.kind === 'app') {
    if (!release.view || !release.skill) {
      throw new Error(`App release ${release.name} needs a view and skill`)
    }
    const dir = `packages/${encodeURIComponent(release.lineageId)}/${encodeURIComponent(release.version)}`
    const viewId = await releaseDocument(repo, target.projectId, {
      path: `${dir}/view`,
      mime: release.view.mime,
      title: `${release.name} view`,
    })
    await publishArtifact(repo, target.projectId, viewId, release.view.content)
    const skillId = await releaseDocument(repo, target.projectId, {
      path: `${dir}/skill.md`,
      mime: 'text/markdown',
      title: `${release.name} skill`,
      content: release.skill.content,
    })
    await updateApp(repo, target.projectId, appId, {
      name: release.name,
      description: release.description,
      schema: plain(release.schema ?? { fields: {} }),
      viewDocId: viewId,
      skillDocId: skillId,
      runtime: release.runtime,
      capabilities: plain(release.capabilities),
      handles: plain(release.handles ?? []),
      panels: plain(release.panels ?? []),
      presentation: release.presentation ?? 'application',
      instancesOpenable: release.instancesOpenable ?? false,
      // A full release says everything the app is, so what it omits is
      // CLEARED (null), not kept: an update must match a fresh install.
      realm: release.realm ?? null,
      sync: release.sync ?? null,
      icon: release.icon ? plain(release.icon) : null,
      workerName: release.workerName ?? null,
      catalogId,
    })
  } else if (release.kind === 'layout') {
    if (!release.layout)
      throw new Error(`Layout release ${release.name} needs slots`)
    await setLayoutSlots(repo, catalogId, appId, plain(release.layout.slots))
    await setLayoutGeometry(
      repo,
      catalogId,
      appId,
      plain(release.layout.geometry ?? {}),
    )
  } else {
    // Chrome and thread renderers keep their module in a document the shell
    // watches, so the new code goes there. The declared settings follow on
    // both copies, or Settings keeps offering the old version's list.
    if (!release.view)
      throw new Error(`${release.kind} release ${release.name} needs a view`)
    const viewDocId = (app as { viewDocId?: string }).viewDocId
    if (!viewDocId) throw new Error(`App ${appId} has no module document`)
    const module = await repo.find<{ content: string }>(
      viewDocId as AnyDocumentId,
    )
    module.change((d) => {
      d.content = release.view?.content ?? ''
    })
    await updateApp(repo, target.projectId, appId, {
      name: release.name,
      description: release.description,
      catalogId,
    })
    const settings = release.settings?.length ? plain(release.settings) : null
    const writeSettings = (d: { settings?: SettingSpec[] }) => {
      if (settings) d.settings = settings
      else delete d.settings
    }
    const appHandle = await repo.find<AppDoc>(appId as AnyDocumentId)
    appHandle.change((d) => writeSettings(d as { settings?: SettingSpec[] }))
    const catalogHandle = await repo.find<ProjectDoc>(
      catalogId as AnyDocumentId,
    )
    changeProject(catalogHandle, (d) => {
      const ref = d.apps?.[appId]
      if (ref) writeSettings(ref as { settings?: SettingSpec[] })
    })
  }

  const handle = await repo.find<AppDoc>(appId as AnyDocumentId)
  handle.change((d) => {
    d.installedFrom = {
      lineageId: release.lineageId,
      version: release.version,
      releaseId,
    }
  })
}
